<template>
  <div class="project" v-if="project">
    <el-container>
      <el-header height="60px">
        <el-button class="header-btn pull-right"
                   type="primary"
                   icon="el-icon-circle-plus"
                   @click="createTask"> Create New Task</el-button>
        <div class="card-top">
          <div class="card-logo">
            <img v-if="project.client_icon_url" :src="project.client_icon_url" class="client-code"/>
            <client-code v-if="!project.client_icon_url" :code="project.client_code" :seed="project.client_name"/>
          </div>
          <div class="card-title">
            <h2 class="card-title" @click="editSelectedProject">
              {{ project.project_code }} - {{ project.name }} <i class="el-icon-edit-outline"></i>
            </h2>
            <div class="card-subtitle">
              <router-link :to="{ name: 'client', params: { client_id: project.client_id }}">
                <b class="bold">{{ projectStatusLabel }} Project</b> for <b>{{ project.client_name }}</b>
              </router-link>
            </div>
          </div>
        </div>
      </el-header>
      <el-main class="panels">
        <div class="panel">
          <div class="tracking-panel panel list-panel">
            <el-card class="box-card">
              <div slot="header" class="clearfix">
                <el-radio-group v-model="trackingType" size="mini" class="pull-right">
                  <el-radio-button label="Hours"></el-radio-button>
                  <el-radio-button label="Cost"></el-radio-button>
                </el-radio-group>
                <span class="panel-title"><b>{{ trackingType }} Tracked</b></span>
              </div>

              <div class="list-group">
                <el-row class="list-item">
                  <el-col :span="3" class="tracking-title">
                    <b>Project Total:</b>
                  </el-col>
                  <el-col :span="21">
                    <progress-bar :value="secondsToHours(project.total_time)"
                                  :max-value="project.est_hours > 0 ? project.est_hours : secondsToHours(project.total_time)"
                                  label-suffix="hrs"
                                  v-if="trackingType === 'Hours'"
                    />
                    <progress-bar :value="costUsed(project.total_time)"
                                  :max-value="project.est_hours > 0 ? estimatedCost(project.est_hours) : costUsed(project.total_time)"
                                  :format-currency="true"
                                  v-if="trackingType !== 'Hours'"
                    />
                  </el-col>
                </el-row>

                <el-row class="list-item tag-list" v-if="projectTags.length">
                  <el-col :span="12"
                          v-for="(group, index) in projectTagsGroup"
                          :key="'group' + index">
                    <el-row v-for="tag in group"
                            class="tag-row"
                            :key="tag.id">
                      <el-col :span="6" class="tracking-title">
                        {{ tag.tag }}:
                      </el-col>
                      <el-col :span="18">
                        <progress-bar :value="secondsToHours(tag.total_time)"
                                      :max-value="tag.est_hours > 0 ? tag.est_hours : secondsToHours(tag.total_time)"
                                      label-suffix="hrs"
                                      :height="15"
                                      v-if="trackingType === 'Hours'"
                        />
                        <progress-bar :value="costUsed(tag.total_time)"
                                      :max-value="tag.est_hours > 0 ? estimatedCost(tag.est_hours) : costUsed(tag.total_time)"
                                      :format-currency="true"
                                      :height="15"
                                      v-if="trackingType !== 'Hours'"
                        />
                      </el-col>
                    </el-row>
                  </el-col>
                </el-row>
              </div>
            </el-card>
          </div>
          <task-list :show-status-tag="true"
                     :tasks="projectTasks"
                     :project="project" />
        </div>


        <log-list class="panel-right"
                  :logs="projectLogs"
                  :show-project-title="false"
                  v-if="project" />
      </el-main>
    </el-container>
    <project-crud :project="project"
                  v-if="showProjectCrud"
                  @close="projectCrudClose" />

    <task-crud v-if="showTaskCrud"
               :projectId="project.id"
               @close="taskCrudClose" />
  </div>
</template>

<script>
  import { mixin as LogTimes } from '../../mixins/LogTimes'
  import { mixin as Utils } from '../../mixins/Utils'

  export default {
    name: 'project',
    mixins: [LogTimes, Utils],
    data () {
      return {
        showProjectCrud: false,
        showTaskCrud: false,
        trackingType: 'Hours'
      }
    },
    methods: {
      editSelectedProject () {
        this.showProjectCrud = true
      },
      createTask () {
        this.showTaskCrud = true
      },
      projectCrudClose () {
        // Handle the Project Crud Dialog closing
        this.showProjectCrud = false
      },
      taskCrudClose (task) {
        // Handle the Project Crud Dialog closing
        this.showTaskCrud = false

        if (task) {
          this.$router.push({name: 'task', params: { task_id: task.id }})
        }
      },
      setPageTitle () {
        if (this.project) {
          this.$store.dispatch('setPageTitle', 'Projects')
        }
      },
      // Nav handling
      gotoClient () {
        this.$router.push({name: 'client', params: { client_id: this.project.client_id }})
      },
      gotoUser (userId) {
        this.$router.push({name: 'user', params: { user_id: userId }})
      },

      // Time/Cost related helpers
      costUsed (totalTime) {
        return this.secondsToHours(totalTime) * this.project.hourly_rate
      },
      estimatedCost (estHours) {
        return estHours * this.project.hourly_rate
      },
      secondsToHours (totalTime) {
        return this.$options.filters.secondsToHours(totalTime)
      },
    },
    computed: {
      currentUser () {
        return this.$store.state.users.currentUser
      },

      project () {
        return this.projects[this.routeParamProjectId]
      },

      projectStatusLabel () {
        if (!this.project.status && this.project.prospect) {
          // Closed and never approved
          return 'Unsuccessful'
        } else if (!this.project.status && !this.project.prospect) {
          // Closed and approved
          return 'Completed'
        } else if (this.project.status && !this.project.prospect) {
          // Open and approved
          return 'Approved'
        }
        // Open and not approved
        return 'Prospective'
      },

      projectTasks () {
        // Return all tasks related to the selected project.. if selected
        var tasks = []
        if (this.project) {
          for (var key in this.tasks) {
            if (this.tasks[key].project_id === this.project.id) {
              tasks.push(this.tasks[key])
            }
          }
        }
        return tasks
      },
      projectLogs () {
        // Return all logs related to the selected project via all related tasks.. if selected
        var logs = []
        if (this.project) {
          for (var i in this.projectTasks) {
            logs = logs.concat(Object.keys(this.logs).map((k) => this.logs[k]).filter(function (item) {
              return this.projectTasks[i].id === item.task_id
            }.bind(this)))
          }
        }
        return logs
      },

      projectTags () {
        if (this.project && this.project.tags) {
          return this.project.tags
        }

        return []
      },

      projectTagsGroup () {
        // Group project tags into multiple columns
        let groups = [[]]

        if (this.projectTags.length > 1) {
          groups.push([])
        }

        for (let i = 0; i < this.projectTags.length; i++) {
          if (i % 2 === 0) {
            groups[0].push(this.projectTags[i])
          } else {
            groups[1].push(this.projectTags[i])
          }
        }

        return groups
      },

      // Vuex Mappings
      projects () {
        return this.$store.state.projects.objects
      },
      tasks () {
        return this.$store.state.tasks.objects
      },
      logs () {
        return this.$store.state.logs.objects
      },

      // Vue Router Params
      routeParamProjectId () {
        return this.$route.params.project_id
      }
    },
    mounted () {
      // Note: Intentionally not calling store.getUsers as this is handled on load in App.vue
      // and Pusher should take care of live-updates to users
      this.$store.dispatch('setPageTitle', 'Projects')

      this.$store.dispatch('getProject', this.routeParamProjectId)
      this.$store.dispatch('getTasks', { project_id: this.routeParamProjectId })
      this.$store.dispatch('getLogs', { project_id: this.routeParamProjectId })
    },
    watch: {
      // When the route is programatically changed (e.g. when a different project is selected)
      // the route 'project_id' param (this.projectId) will change.
      // Re-init the Component's data when this happens
      project () {
        this.setPageTitle()
      }
    }
  }
</script>

<style lang="less" rel="stylesheet/less" scoped>
  .card-title {
    cursor: pointer;
  }
  .project {
    .avatars {
      margin-bottom: 5px;
    }

    .el-main {
      max-height: ~'calc(100vh - 125px)';
    }
  }
  .project-components {
    line-height: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    height: 60px;

    .el-tag {
      flex: 0;
      width: 200px;
    }
  }

  .card-top {
    .card-logo,
    .card-title {
      display: inline-block;
      vertical-align: middle;
      line-height: 1.3rem;
    }
    .card-logo {
      margin-right: 10px;
    }

    .card-subtitle {
      font-style: italic;
      font-size: 0.9rem;
    }
  }
</style>

<style lang="less" rel="stylesheet/less">
  .tracking-panel {
    margin-bottom: 20px;
    .el-card__header {
      padding-top: 14px;
      padding-bottom: 15px;
    }
    .panel-title {
      display: inline-block;
      margin-top: 4px;
    }
    .tracking-title {
      text-align: right;
      padding-right: 10px;
    }
    .tag-list {
      padding-top: 7px;
      padding-bottom: 7px;
    }
    .tag-row {
      padding: 5px 0;
    }
  }
</style>
